/**
 * "What It May Do": one row per kind of work, each reading as the action it
 * gates ("Fills USCIS forms") with an Allow / Ask First control.
 *
 * The stored value is unchanged: `authority.confirm_before` lists the
 * backend's area names (`/skills/options` `confirm_before_areas`); a name in
 * the list means Ask First, a name left out means Allow. Only the words and
 * grouping here are the UI's. The backend offers only areas with changes in
 * them (reads are never gated), so every row says what it changes.
 */
import type { FC } from "react";

import { cn } from "@/lib/utils";

/** What each area's gated tools do, as a verb phrase. Keyed by the backend's name. */
export const AREA_ACTION: Record<string, string> = {
  "drafting documents": "Drafts and edits documents",
  "USCIS forms": "Fills USCIS forms",
  "exhibits and packet assembly": "Assembles exhibits and packets",
  "editing PDFs": "Edits PDFs",
  "editing spreadsheets and slides": "Edits spreadsheets and slides",
  "case files": "Changes case files",
  "organizing case files": "Sorts and renames case files into folders",
  "files attached in the chat": "Changes files attached in the chat",
  "the case record": "Changes the case's status and deadline",
  "the client's profile": "Changes the client's profile",
  "the client's records":
    "Changes the client's address, work, status and travel records",
  "the client's documents": "Files, sends or deletes the client's documents",
  "client intakes": "Builds and sends client intakes",
  "legal and policy research": "Starts legal and policy research",
  "USCIS case status": "Tracks USCIS receipts and case status"
};

/** An area as the action it gates; an area the table doesn't know reads "Works on …". */
export const areaAction = (area: string): string =>
  AREA_ACTION[area] ?? `Works on ${area}`;

type Group = { title: string; areas: readonly string[] };

const GROUPS: readonly Group[] = [
  {
    title: "Documents and Filings",
    areas: [
      "drafting documents",
      "USCIS forms",
      "exhibits and packet assembly",
      "editing PDFs",
      "editing spreadsheets and slides"
    ]
  },
  {
    title: "Case Files and Record",
    areas: [
      "case files",
      "organizing case files",
      "files attached in the chat",
      "the case record"
    ]
  },
  {
    title: "Client Information",
    areas: [
      "the client's profile",
      "the client's records",
      "the client's documents",
      "client intakes"
    ]
  },
  {
    title: "Research and Tracking",
    areas: ["legal and policy research", "USCIS case status"]
  }
];

/** The offered areas in groups; any the table doesn't place go last, under Other. */
export const groupAreas = (
  areas: readonly string[]
): Array<{ title: string; areas: string[] }> => {
  const placed = new Set(GROUPS.flatMap((g) => g.areas));
  const groups = GROUPS.map((g) => ({
    title: g.title,
    areas: g.areas.filter((a) => areas.includes(a))
  }));
  groups.push({
    title: "Other",
    areas: areas.filter((a) => !placed.has(a))
  });
  return groups.filter((g) => g.areas.length > 0);
};

/** `confirm-USCIS-forms`: the Ask First button's test id (and `allow-…` its pair). */
export const areaSlug = (area: string): string => area.replace(/\W+/g, "-");

const Segment: FC<{
  label: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  testId: string;
}> = ({ label, active, disabled, onClick, testId }) => (
  <button
    type="button"
    role="radio"
    aria-checked={active}
    disabled={disabled}
    onClick={onClick}
    data-testid={testId}
    className={cn(
      "h-6 rounded-sm px-2.5 text-sm font-medium whitespace-nowrap text-fg-3 outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-60",
      "aria-checked:bg-elevated aria-checked:text-fg aria-checked:ring-1 aria-checked:ring-line-2"
    )}
  >
    {label}
  </button>
);

/** One row: what the work is, and whether it goes ahead (Allow) or waits for approval (Ask First). */
export const AreaRow: FC<{
  area: string;
  askFirst: boolean;
  disabled?: boolean;
  onChange: (askFirst: boolean) => void;
}> = ({ area, askFirst, disabled, onChange }) => {
  const slug = areaSlug(area);
  const action = areaAction(area);
  return (
    <div
      className="flex items-center justify-between gap-3 py-1"
      data-testid={`area-${slug}`}
      data-ask-first={askFirst || undefined}
    >
      <span className="min-w-0 text-base text-fg">{action}</span>
      <div
        role="radiogroup"
        aria-label={`${action}: Allow or Ask First`}
        className="inline-flex h-7 shrink-0 items-center gap-0.5 rounded-md bg-fill-1 p-0.5"
      >
        <Segment
          label="Allow"
          active={!askFirst}
          disabled={disabled}
          onClick={() => onChange(false)}
          testId={`allow-${slug}`}
        />
        <Segment
          label="Ask First"
          active={askFirst}
          disabled={disabled}
          onClick={() => onChange(true)}
          testId={`confirm-${slug}`}
        />
      </div>
    </div>
  );
};
